import { useEffect, useState } from 'react';
import { scheduleService } from '@/services/schedule.service';
import { countTodayItems } from '@/lib/todayCount';
import { useTodayKey } from './useTodayKey';
import type { EventRow, TaskRow } from '@/lib/schedule';

/**
 * How many items the household has on Today (YAR-399).
 *
 * Backs the drawer's Today badge. It has its own fetch rather than reading
 * TodayTab's state because the badge lives in the app chrome: it has to be
 * right on Schedule, Review and Settings too, and a user who never opens
 * Today would otherwise see no badge at all.
 *
 * Correctness against the page is preserved by `countTodayItems`, which sums
 * the same partitioning functions the section headers count, over the same
 * two service queries the page issues.
 *
 * `useTodayKey` re-renders this at local midnight, so the badge rolls over
 * with the day instead of holding yesterday's number until a reload — the
 * same reason DateStrip and MiniCalendar use it.
 *
 * Returns `undefined` while loading; NavCountBadge renders nothing for it,
 * so the pill appears once rather than flashing a zero first.
 *
 * Bump `refreshKey` after a mutation this hook cannot observe — completing a
 * task on Today, approving an email — to force a re-count.
 */
export function useTodayCount(
  householdId: string | null | undefined,
  timezone?: string | null,
  refreshKey?: number,
): number | undefined {
  const [count, setCount] = useState<number | undefined>(undefined);
  const todayKey = useTodayKey(timezone);

  useEffect(() => {
    if (!householdId) {
      setCount(undefined);
      return;
    }
    let cancelled = false;

    void (async () => {
      try {
        const [tasks, events] = await Promise.all([
          scheduleService.getTodayTasks(householdId, timezone),
          scheduleService.getTodayEvents(householdId, timezone),
        ]);
        if (cancelled) return;
        setCount(
          countTodayItems(
            tasks as TaskRow[],
            events as EventRow[],
            todayKey,
            timezone,
          ),
        );
      } catch {
        // A chrome badge must never take the app down, and a stale number is
        // worse than none: drop back to "unknown" so nothing is rendered.
        if (!cancelled) setCount(undefined);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [householdId, timezone, todayKey, refreshKey]);

  return count;
}
